import React, { useState, useEffect } from 'react';
import { X, Check, Trash2, Video, AlertCircle, Car } from 'lucide-react';
import { createEvent, updateEvent, deleteEvent, getCalendars, getEvent } from '../api';
import { Button } from './catalyst/button';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from './catalyst/dialog';
import { Divider } from './catalyst/divider';
import { Input } from './catalyst/input';
import { Select } from './catalyst/select';
import { Switch } from './catalyst/switch';
import { Textarea } from './catalyst/textarea';

// Recurring event dialog component
function RecurringEventDialog({ onChoice, onCancel, action = 'edit' }) {
  return (
    <Dialog size="md" onClose={onCancel}>
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-zinc-500" />
        <DialogTitle>{action === 'delete' ? 'Delete recurring event' : 'Edit recurring event'}</DialogTitle>
      </div>
      <DialogDescription>
        Choose whether to {action} this occurrence or the full series.
      </DialogDescription>
      <DialogBody className="space-y-2">
        <button
          type="button"
          onClick={() => onChoice('single')}
          className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50"
        >
          <div className="font-medium text-zinc-950">This event only</div>
          <div className="text-xs text-zinc-500">Only {action} this occurrence</div>
        </button>
        <button
          type="button"
          onClick={() => onChoice('all')}
          className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50"
        >
          <div className="font-medium text-zinc-950">All events</div>
          <div className="text-xs text-zinc-500">{action === 'delete' ? 'Delete' : 'Edit'} all events in the series</div>
        </button>
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// Delete confirmation dialog component
function DeleteConfirmDialog({ onConfirm, onCancel, eventTitle }) {
  return (
    <Dialog size="md" onClose={onCancel}>
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-600" />
        <DialogTitle>Delete event?</DialogTitle>
      </div>
      <DialogDescription>
        "{eventTitle}" will be permanently deleted.
      </DialogDescription>
      <DialogActions>
        <Button outline onClick={onCancel}>Cancel</Button>
        <Button color="red" onClick={onConfirm}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

const EVENT_COLORS = [
  { id: '1', name: 'Blue', bg: '#2563EB', hover: '#1D4ED8' },
  { id: '2', name: 'Rose', bg: '#E11D48', hover: '#BE123C' },
  { id: '3', name: 'Emerald', bg: '#059669', hover: '#047857' },
  { id: '4', name: 'Zinc', bg: '#71717A', hover: '#52525B' }
];

const TRAVEL_TIME_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '5', label: '5 minutes' },
  { value: '10', label: '10 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1 hour 30 min' },
  { value: '120', label: '2 hours' }
];

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Never' },
  { value: 'daily', label: 'Every Day' },
  { value: 'weekly', label: 'Every Week' },
  { value: 'customWeekly', label: 'Custom Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Every Month' },
  { value: 'yearly', label: 'Every Year' }
];

const WEEKDAY_OPTIONS = [
  { value: 'MO', label: 'M', name: 'Monday' },
  { value: 'TU', label: 'T', name: 'Tuesday' },
  { value: 'WE', label: 'W', name: 'Wednesday' },
  { value: 'TH', label: 'T', name: 'Thursday' },
  { value: 'FR', label: 'F', name: 'Friday' },
  { value: 'SA', label: 'S', name: 'Saturday' },
  { value: 'SU', label: 'S', name: 'Sunday' }
];

const DATE_TO_WEEKDAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const WEEKDAY_VALUES = WEEKDAY_OPTIONS.map(day => day.value);

function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatLocalDate(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatLocalTime(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function addDaysToDateString(dateStr, days) {
  const date = parseLocalDate(dateStr);
  if (!date) return dateStr;
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

function getInclusiveAllDayEndDate(exclusiveEndDate, startDate) {
  if (!exclusiveEndDate) return startDate;
  const inclusiveEndDate = addDaysToDateString(exclusiveEndDate, -1);
  return inclusiveEndDate < startDate ? startDate : inclusiveEndDate;
}

function getWeekdayForDate(dateStr) {
  const date = parseLocalDate(dateStr);
  return date ? DATE_TO_WEEKDAY[date.getDay()] : 'MO';
}

function sortWeekdays(days) {
  const selected = new Set(days);
  return WEEKDAY_VALUES.filter(day => selected.has(day));
}

function getDefaultCustomRepeatDays(startDate) {
  return [getWeekdayForDate(startDate)];
}

function getRRuleParts(rrule) {
  return (rrule || '')
    .replace(/^RRULE:/i, '')
    .split(';')
    .reduce((parts, part) => {
      const [key, ...valueParts] = part.split('=');
      if (!key || valueParts.length === 0) return parts;
      return { ...parts, [key.toUpperCase()]: valueParts.join('=').toUpperCase() };
    }, {});
}

function parseByDay(value) {
  if (!value) return [];
  return sortWeekdays(
    value
      .split(',')
      .map(day => day.replace(/^-?\d+/, ''))
      .filter(day => WEEKDAY_VALUES.includes(day))
  );
}

function parseRecurrenceRules(recurrenceRules) {
  const rrule = recurrenceRules?.find(rule => /^RRULE:/i.test(rule));
  if (!rrule) return { recurrence: 'none', customRepeatDays: [] };

  const parts = getRRuleParts(rrule);
  const interval = parts.INTERVAL || '1';
  const customRepeatDays = parseByDay(parts.BYDAY);

  if (parts.FREQ === 'DAILY') return { recurrence: 'daily', customRepeatDays: [] };
  if (parts.FREQ === 'WEEKLY') {
    if (customRepeatDays.length > 0 && interval === '1') {
      return { recurrence: 'customWeekly', customRepeatDays };
    }
    if (interval === '2') return { recurrence: 'biweekly', customRepeatDays: [] };
    return { recurrence: 'weekly', customRepeatDays: [] };
  }
  if (parts.FREQ === 'MONTHLY') return { recurrence: 'monthly', customRepeatDays: [] };
  if (parts.FREQ === 'YEARLY') return { recurrence: 'yearly', customRepeatDays: [] };

  return { recurrence: 'none', customRepeatDays: [] };
}

function buildRecurrenceRules(formData) {
  switch (formData.recurrence) {
    case 'daily':
      return ['RRULE:FREQ=DAILY'];
    case 'weekly':
      return ['RRULE:FREQ=WEEKLY'];
    case 'customWeekly': {
      const days = sortWeekdays(
        formData.customRepeatDays.length > 0
          ? formData.customRepeatDays
          : getDefaultCustomRepeatDays(formData.startDate)
      );
      return [`RRULE:FREQ=WEEKLY;BYDAY=${days.join(',')}`];
    }
    case 'biweekly':
      return ['RRULE:FREQ=WEEKLY;INTERVAL=2'];
    case 'monthly':
      return ['RRULE:FREQ=MONTHLY'];
    case 'yearly':
      return ['RRULE:FREQ=YEARLY'];
    default:
      return [];
  }
}

// Helper: format time as "09:00" or "23:00" (24-hour)
function formatTimeDisplay(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// iOS-style scroll wheel column
function WheelColumn({ items, selectedIndex, onSelect, width = '50%' }) {
  const containerRef = React.useRef(null);
  const ITEM_HEIGHT = 40;
  const VISIBLE_ITEMS = 5;
  const isScrollingRef = React.useRef(false);
  const scrollTimeoutRef = React.useRef(null);

  // Scroll to selected index on mount
  React.useEffect(() => {
    if (containerRef.current && !isScrollingRef.current) {
      containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }
  }, [selectedIndex]);

  const handleScroll = () => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    isScrollingRef.current = true;
    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      const scrollTop = containerRef.current.scrollTop;
      const idx = Math.round(scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      containerRef.current.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: 'smooth' });
      isScrollingRef.current = false;
      if (clamped !== selectedIndex) onSelect(clamped);
    }, 80);
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        width,
        height: ITEM_HEIGHT * VISIBLE_ITEMS,
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      {/* Top padding to center first item */}
      <div style={{ height: ITEM_HEIGHT * 2 }} />
      {items.map((item, i) => {
        const isSelected = i === selectedIndex;
        const dist = Math.abs(i - selectedIndex);
        return (
          <div
            key={i}
            onClick={() => {
              onSelect(i);
              if (containerRef.current) {
                containerRef.current.scrollTo({ top: i * ITEM_HEIGHT, behavior: 'smooth' });
              }
            }}
            style={{
              height: ITEM_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isSelected ? '22px' : '18px',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? '#000' : dist === 1 ? '#555' : '#BBB',
              scrollSnapAlign: 'center',
              cursor: 'pointer',
              transition: 'color 0.15s, font-size 0.15s',
              userSelect: 'none',
            }}
          >
            {item}
          </div>
        );
      })}
      {/* Bottom padding to center last item */}
      <div style={{ height: ITEM_HEIGHT * 2 }} />
    </div>
  );
}

// iOS-style time picker with hour/minute scroll wheels
function TimeScrollPicker({ value, onChange }) {
  const [h, m] = (value || '09:00').split(':').map(Number);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];
  const minuteIdx = minutes.reduce((best, v, i) => Math.abs(v - m) < Math.abs(minutes[best] - m) ? i : best, 0);

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
      }}>
        {/* Selection highlight bar */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '180px',
          height: '40px',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#E8E8ED',
          borderRadius: '10px',
          zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', width: '180px' }}>
          <WheelColumn
            items={hours.map(hh => String(hh).padStart(2, '0'))}
            selectedIndex={h}
            width="50%"
            onSelect={(idx) => {
              const newH = String(idx).padStart(2, '0');
              const newM = String(minutes[minuteIdx]).padStart(2, '0');
              onChange(`${newH}:${newM}`);
            }}
          />
          <WheelColumn
            items={minutes.map(mm => String(mm).padStart(2, '0'))}
            selectedIndex={minuteIdx}
            width="50%"
            onSelect={(idx) => {
              const newH = String(h).padStart(2, '0');
              const newM = String(minutes[idx]).padStart(2, '0');
              onChange(`${newH}:${newM}`);
            }}
          />
        </div>
      </div>
    </div>
  );
}

// iOS-style toggle switch
function ToggleSwitch({ checked, onChange }) {
  return <Switch checked={checked} onChange={onChange} color="emerald" />;
}

function WeekdaySelector({ selectedDays, onToggle }) {
  return (
    <div className="grid grid-cols-7 gap-1.5 px-4 py-3">
      {WEEKDAY_OPTIONS.map(day => {
        const selected = selectedDays.includes(day.value);
        return (
          <button
            key={day.value}
            type="button"
            aria-pressed={selected}
            aria-label={day.name}
            title={day.name}
            onClick={() => onToggle(day.value)}
            className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-zinc-950 ${
              selected
                ? 'border-zinc-950 bg-zinc-950 text-white'
                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
            }`}
          >
            {day.label}
          </button>
        );
      })}
    </div>
  );
}

function CardSection({ children, className = '' }) {
  return (
    <div className={`mb-3 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function CardRow({ label, value, onClick, children, showDivider = true, rightContent }) {
  const content = (
    <div
      className={`flex min-h-[52px] items-center justify-between gap-4 px-4 py-3 ${onClick ? 'cursor-pointer transition hover:bg-zinc-50 active:bg-zinc-100' : ''}`}
      onClick={onClick}
    >
      <span className="text-sm font-medium text-zinc-950">{label}</span>
      <div className="flex min-w-0 items-center justify-end gap-2">
        {rightContent || (
          value !== undefined && (
            <span className="truncate text-sm text-zinc-500">{value}</span>
          )
        )}
        {children}
      </div>
    </div>
  );
  return (
    <>
      {content}
      {showDivider && <Divider soft />}
    </>
  );
}

export default function EventModal({ event, onClose, onSave }) {
  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    location: '',
    startDate: '',
    startTime: '09:00',
    endDate: '',
    endTime: '10:00',
    allDay: false,
    colorId: '1',
    recurrence: 'none',
    customRepeatDays: [],
    calendarId: 'primary',
    travelBefore: '0',
    travelAfter: '0'
  });
  const [animateIn, setAnimateIn] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [recurringAction, setRecurringAction] = useState('edit');
  const [pendingEventData, setPendingEventData] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTimePicker, setActiveTimePicker] = useState(null); // 'start' | 'end' | null
  const [loadingRecurrence, setLoadingRecurrence] = useState(false);

  const isRecurringEvent = event?.recurringEventId || (event?.recurrence && event.recurrence.length > 0);
  const isEditMode = !!event?.id;

  // Animate in on mount + prevent background scroll on mobile
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimateIn(true));
    });
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleClose = () => {
    setAnimateIn(false);
    setTimeout(() => onClose(), 450);
  };

  // Fetch calendars on mount
  useEffect(() => {
    const fetchCalendarList = async () => {
      try {
        const data = await getCalendars();
        const writableCalendars = data.filter(cal => cal.accessRole === 'owner' || cal.accessRole === 'writer');
        setCalendars(writableCalendars);
      } catch (error) {
        console.error('Error fetching calendars:', error);
      }
    };
    fetchCalendarList();
  }, []);

  useEffect(() => {
    if (event) {
      const isAllDay = !event.start?.dateTime;
      const fallbackDate = formatLocalDate(new Date());
      const start = event.start?.dateTime ? new Date(event.start.dateTime) : null;
      const end = event.end?.dateTime ? new Date(event.end.dateTime) : null;
      const startDate = isAllDay
        ? (event.start?.date || fallbackDate)
        : (formatLocalDate(start) || fallbackDate);
      const endDate = isAllDay
        ? getInclusiveAllDayEndDate(event.end?.date, startDate)
        : (formatLocalDate(end) || startDate);

      const recurrenceInfo = parseRecurrenceRules(event.recurrence);

      const travelBefore = event.extendedProperties?.shared?.travelBefore || '0';
      const travelAfter = event.extendedProperties?.shared?.travelAfter || '0';

      setFormData({
        summary: event.summary || '',
        description: event.description || '',
        location: event.location || '',
        startDate,
        startTime: event.start?.dateTime
          ? (formatLocalTime(start) || '09:00')
          : '09:00',
        endDate,
        endTime: event.end?.dateTime
          ? (formatLocalTime(end) || '10:00')
          : '10:00',
        allDay: isAllDay,
        colorId: event.colorId || '1',
        recurrence: recurrenceInfo.recurrence,
        customRepeatDays: recurrenceInfo.customRepeatDays,
        calendarId: event.calendarId || 'primary',
        travelBefore,
        travelAfter
      });
    }
  }, [event]);

  useEffect(() => {
    if (!event?.recurringEventId || event.recurrence?.length) {
      setLoadingRecurrence(false);
      return;
    }

    let cancelled = false;
    const calendarId = event.calendarId || event.organizer?.email || 'primary';
    setLoadingRecurrence(true);

    getEvent(event.recurringEventId, calendarId)
      .then(masterEvent => {
        if (cancelled) return;
        const recurrenceInfo = parseRecurrenceRules(masterEvent.recurrence);
        setFormData(prev => {
          if (prev.recurrence !== 'none' || prev.customRepeatDays.length > 0) return prev;
          return {
            ...prev,
            recurrence: recurrenceInfo.recurrence,
            customRepeatDays: recurrenceInfo.customRepeatDays
          };
        });
      })
      .catch(error => {
        console.error('Error fetching recurring event details:', error);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecurrence(false);
      });

    return () => {
      cancelled = true;
    };
  }, [event]);

  // When start changes, auto-adjust end to be at least 1 hour after
  const handleStartDateChange = (newDate) => {
    const updated = { ...formData, startDate: newDate };
    // If end date is before start date, adjust
    if (newDate > formData.endDate) {
      updated.endDate = newDate;
    }
    setFormData(updated);
  };

  const handleRecurrenceChange = (recurrence) => {
    setFormData(prev => ({
      ...prev,
      recurrence,
      customRepeatDays: recurrence === 'customWeekly' && prev.customRepeatDays.length === 0
        ? getDefaultCustomRepeatDays(prev.startDate)
        : prev.customRepeatDays
    }));
  };

  const handleCustomRepeatDayToggle = (day) => {
    setFormData(prev => {
      const repeatDays = prev.customRepeatDays.length > 0
        ? prev.customRepeatDays
        : getDefaultCustomRepeatDays(prev.startDate);
      const selected = repeatDays.includes(day);
      if (selected && repeatDays.length === 1) {
        return { ...prev, customRepeatDays: repeatDays };
      }

      return {
        ...prev,
        customRepeatDays: sortWeekdays(
          selected
            ? repeatDays.filter(selectedDay => selectedDay !== day)
            : [...repeatDays, day]
        )
      };
    });
  };

  const handleStartTimeChange = (newTime) => {
    const updated = { ...formData, startTime: newTime };
    // If same day and end time is <= start time, push end time +1hr
    if (formData.startDate === formData.endDate) {
      const [sh, sm] = newTime.split(':').map(Number);
      const [eh, em] = formData.endTime.split(':').map(Number);
      if (eh * 60 + em <= sh * 60 + sm) {
        const newEnd = sh * 60 + sm + 60;
        if (newEnd >= 24 * 60) {
          // Push to next day
          updated.endDate = addDaysToDateString(formData.startDate, 1);
          updated.endTime = '00:00';
        } else {
          const nh = Math.floor(newEnd / 60);
          const nm = newEnd % 60;
          updated.endTime = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
        }
      }
    }
    setFormData(updated);
  };

  const handleEndDateChange = (newDate) => {
    if (newDate < formData.startDate) return;
    setFormData({ ...formData, endDate: newDate });
  };

  const handleEndTimeChange = (newTime) => {
    setFormData({ ...formData, endTime: newTime });
  };

  const handleAllDayToggle = (checked) => {
    setFormData({ ...formData, allDay: checked });
  };

  const buildEventData = () => {
    const eventData = {
      summary: formData.summary,
      description: formData.description,
      location: formData.location,
      start: {},
      end: {},
      colorId: formData.colorId
    };

    if (formData.allDay) {
      eventData.start.date = formData.startDate;
      // For all-day events, end date is exclusive (next day)
      eventData.end.date = addDaysToDateString(formData.endDate || formData.startDate, 1);
    } else {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Send local datetime with timeZone — avoids UTC conversion issues
      // that can cause Google to strip recurrence on overnight events
      eventData.start.dateTime = `${formData.startDate}T${formData.startTime}:00`;
      eventData.end.dateTime = `${formData.endDate}T${formData.endTime}:00`;
      eventData.start.timeZone = tz;
      eventData.end.timeZone = tz;
    }

    const privateProperties = event?.extendedProperties?.private || {};

    eventData.extendedProperties = {
      shared: {
        ...(event?.extendedProperties?.shared || {}),
        travelBefore: formData.travelBefore !== '0' ? formData.travelBefore : '0',
        travelAfter: formData.travelAfter !== '0' ? formData.travelAfter : '0'
      },
      ...(Object.keys(privateProperties).length ? { private: privateProperties } : {})
    };

    const recurrenceRules = buildRecurrenceRules(formData);
    if (recurrenceRules.length > 0) {
      eventData.recurrence = recurrenceRules;
    }

    return eventData;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loadingRecurrence) return;

    try {
      const eventData = buildEventData();

      if (event?.id) {
        if (isRecurringEvent) {
          setPendingEventData(eventData);
          setRecurringAction('edit');
          setShowRecurringDialog(true);
          return;
        }
        await updateEvent(event.id, { ...eventData, calendarId: event.calendarId });
      } else {
        await createEvent({ ...eventData, calendarId: formData.calendarId });
      }

      onSave();
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Failed to save event: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  const handleRecurringChoice = async (choice) => {
    setShowRecurringDialog(false);

    try {
      if (recurringAction === 'edit') {
        let eventId = event.id;
        if (choice === 'all' && event.recurringEventId) {
          eventId = event.recurringEventId;
        }
        await updateEvent(eventId, {
          ...pendingEventData,
          calendarId: event.calendarId,
          updateScope: choice
        });
      } else if (recurringAction === 'delete') {
        let eventId = event.id;
        if (choice === 'all' && event.recurringEventId) {
          eventId = event.recurringEventId;
        }
        const calendarId = event.calendarId || event.organizer?.email || 'primary';
        await deleteEvent(eventId, calendarId);
      }

      onSave();
      handleClose();
    } catch (error) {
      console.error(`Error ${recurringAction}ing recurring event:`, error);
      alert(`Failed to ${recurringAction} event`);
    }
  };

  const handleDelete = async () => {
    if (isRecurringEvent) {
      setRecurringAction('delete');
      setShowRecurringDialog(true);
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    try {
      handleClose();
      const calendarId = event.calendarId || event.organizer?.email || 'primary';
      await deleteEvent(event.id, calendarId);
      onSave();
    } catch (error) {
      console.error('Error deleting event:', error);

      let errorMessage = 'Failed to delete event';
      if (error.response?.status === 403) {
        errorMessage = 'You don\'t have permission to delete this event.';
      } else if (error.response?.status === 410 || error.response?.status === 404) {
        errorMessage = 'This event has already been deleted.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  const calendarName = (() => {
    const cal = calendars.find(c => c.id === formData.calendarId);
    if (cal) return cal.summary + (cal.primary ? '' : '');
    return formData.calendarId === 'primary' ? 'Primary Calendar' : formData.calendarId;
  })();

  const calendarColor = (() => {
    const cal = calendars.find(c => c.id === formData.calendarId);
    return cal?.backgroundColor || '#34AADC';
  })();

  return (
    <>
      {showRecurringDialog && (
        <RecurringEventDialog
          action={recurringAction}
          onChoice={handleRecurringChoice}
          onCancel={() => setShowRecurringDialog(false)}
        />
      )}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          eventTitle={event?.summary || 'this event'}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      <div
        className="fixed inset-0 z-50"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Backdrop for desktop */}
        <div
          className="absolute inset-0 bg-black hidden md:block transition-opacity duration-300"
          style={{ opacity: animateIn ? 0.5 : 0 }}
          onClick={handleClose}
        />

        <div
          className="h-full w-full overflow-hidden bg-zinc-100 md:relative md:z-10 md:h-auto md:max-h-[90vh] md:max-w-lg md:rounded-2xl md:bg-white md:shadow-2xl md:ring-1 md:ring-zinc-950/10"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            transform: animateIn ? 'translateY(0)' : 'translateY(100%)',
            opacity: animateIn ? 1 : 0,
            transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease'
          }}
        >
          <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white">
            <div className="flex h-16 items-center justify-between px-4">
              <Button
                type="button"
                onClick={handleClose}
                plain
                className="h-9 w-9 rounded-full p-0"
                aria-label="Close event editor"
              >
                <X className="h-4 w-4" strokeWidth={2.5} />
              </Button>
              <span className="text-sm font-semibold text-zinc-950">
                {isEditMode ? 'Edit Event' : 'New'}
              </span>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loadingRecurrence}
                className="h-9 w-9 rounded-full p-0"
                aria-label="Save event"
              >
                <Check className="h-4 w-4" strokeWidth={2.5} />
              </Button>
            </div>
          </div>

          {/* Scrollable content */}
          <div
            className="overflow-y-auto flex-1"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              padding: '8px 16px 32px 16px'
            }}
          >
            <form onSubmit={handleSubmit}>
              <CardSection>
                <div className="p-4">
                  <Input
                    type="text"
                    placeholder="Title"
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    className="border-0 bg-transparent px-0 py-0 text-base font-semibold shadow-none focus:ring-0"
                    required
                  />
                </div>
                <Divider soft />
                <div className="p-4">
                  <Input
                    type="text"
                    placeholder="Location or Video Call"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="border-0 bg-transparent px-0 py-0 text-sm shadow-none focus:ring-0"
                  />
                </div>
              </CardSection>

              {/* All-day, Starts, Ends */}
              <CardSection>
                <CardRow
                  label="All-day"
                  rightContent={
                    <ToggleSwitch
                      checked={formData.allDay}
                      onChange={handleAllDayToggle}
                    />
                  }
                />
                <CardRow label="Starts" showDivider={activeTimePicker !== 'start'}>
                  <div className="flex items-center gap-2">
                    <div className="w-[154px]">
                      <Input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        required
                      />
                    </div>
                    {!formData.allDay && (
                      <Button
                        type="button"
                        outline={activeTimePicker !== 'start'}
                        onClick={() => setActiveTimePicker(activeTimePicker === 'start' ? null : 'start')}
                        color={activeTimePicker === 'start' ? 'dark/zinc' : 'white'}
                        className="whitespace-nowrap px-3 py-2"
                      >
                        {formatTimeDisplay(formData.startTime)}
                      </Button>
                    )}
                  </div>
                </CardRow>
                {activeTimePicker === 'start' && !formData.allDay && (
                  <TimeScrollPicker
                    value={formData.startTime}
                    onChange={(t) => handleStartTimeChange(t)}
                  />
                )}
                <CardRow label="Ends" showDivider={activeTimePicker !== 'end'}>
                  <div className="flex items-center gap-2">
                    <div className="w-[154px]">
                      <Input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        min={formData.startDate}
                      />
                    </div>
                    {!formData.allDay && (
                      <Button
                        type="button"
                        outline={activeTimePicker !== 'end'}
                        onClick={() => setActiveTimePicker(activeTimePicker === 'end' ? null : 'end')}
                        color={activeTimePicker === 'end' ? 'dark/zinc' : 'white'}
                        className="whitespace-nowrap px-3 py-2"
                      >
                        {formatTimeDisplay(formData.endTime)}
                      </Button>
                    )}
                  </div>
                </CardRow>
                {activeTimePicker === 'end' && !formData.allDay && (
                  <TimeScrollPicker
                    value={formData.endTime}
                    onChange={(t) => handleEndTimeChange(t)}
                  />
                )}
              </CardSection>

              {/* Repeat */}
              <CardSection>
                <CardRow label="Repeat" showDivider={false}>
                  <Select
                    value={formData.recurrence}
                    onChange={(e) => handleRecurrenceChange(e.target.value)}
                    className="w-[180px]"
                  >
                    {RECURRENCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                </CardRow>
                {formData.recurrence === 'customWeekly' && (
                  <>
                    <Divider soft />
                    <WeekdaySelector
                      selectedDays={formData.customRepeatDays.length > 0
                        ? formData.customRepeatDays
                        : getDefaultCustomRepeatDays(formData.startDate)}
                      onToggle={handleCustomRepeatDayToggle}
                    />
                  </>
                )}
              </CardSection>

              {/* Travel Time */}
              {!formData.allDay && (
                <CardSection>
                  <CardRow label="Travel before" showDivider={true}>
                    <div className="flex min-w-[180px] items-center gap-2">
                      <Car className="h-4 w-4 text-zinc-400" />
                      <Select
                        value={formData.travelBefore}
                        onChange={(e) => setFormData({ ...formData, travelBefore: e.target.value })}
                        className="flex-1"
                      >
                        {TRAVEL_TIME_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </div>
                  </CardRow>
                  <CardRow label="Travel after" showDivider={false}>
                    <div className="flex min-w-[180px] items-center gap-2">
                      <Car className="h-4 w-4 text-zinc-400" />
                      <Select
                        value={formData.travelAfter}
                        onChange={(e) => setFormData({ ...formData, travelAfter: e.target.value })}
                        className="flex-1"
                      >
                        {TRAVEL_TIME_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Select>
                    </div>
                  </CardRow>
                </CardSection>
              )}

              {/* Calendar */}
              <CardSection>
                <CardRow label="Calendar" showDivider={false}>
                  {isEditMode ? (
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: calendarColor }}
                      />
                      <span className="truncate text-sm text-zinc-500">{calendarName}</span>
                    </div>
                  ) : (
                    <div className="flex min-w-[220px] items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: calendarColor }}
                      />
                      <Select
                        value={formData.calendarId}
                        onChange={(e) => setFormData({ ...formData, calendarId: e.target.value })}
                        className="flex-1"
                      >
                        {calendars.length === 0 ? (
                          <option value="primary">Primary Calendar</option>
                        ) : (
                          calendars.map(cal => (
                            <option key={cal.id} value={cal.id}>
                              {cal.summary}{cal.primary ? ' (Primary)' : ''}
                            </option>
                          ))
                        )}
                      </Select>
                    </div>
                  )}
                </CardRow>
              </CardSection>

              {/* Video call link (if editing an event with one) */}
              {(event?.hangoutLink || event?.conferenceData?.entryPoints?.[0]?.uri) && (
                <CardSection>
                  <div className="flex min-h-[52px] items-center gap-3 px-4 py-3">
                    <Video className="h-5 w-5 text-zinc-400" />
                    <Button
                      href={event.hangoutLink || event.conferenceData.entryPoints[0].uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      outline
                      className="min-w-0"
                    >
                      {(() => {
                        const link = event.hangoutLink || event.conferenceData.entryPoints[0].uri;
                        if (link.includes('meet.google.com')) return 'Join Google Meet';
                        if (link.includes('zoom.us')) return 'Join Zoom Meeting';
                        return 'Join Video Call';
                      })()}
                    </Button>
                  </div>
                </CardSection>
              )}

              {/* Color */}
              <CardSection>
                <div className="flex min-h-[52px] items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-zinc-950">Color</span>
                  <div className="flex gap-2">
                    {EVENT_COLORS.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, colorId: color.id })}
                        className="rounded-full ring-offset-2 transition focus:outline-none focus:ring-2 focus:ring-zinc-950"
                        style={{
                          width: '28px',
                          height: '28px',
                          backgroundColor: color.bg,
                          boxShadow: formData.colorId === color.id ? `0 0 0 2px #fff, 0 0 0 4px ${color.bg}` : 'none'
                        }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </CardSection>

              {/* Notes (description) */}
              <CardSection>
                <div className="p-4">
                  <Textarea
                    placeholder="Notes"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="border-0 bg-transparent px-0 py-0 shadow-none focus:ring-0"
                    rows={4}
                    resizable={false}
                  />
                </div>
              </CardSection>

              {/* Delete button for existing events */}
              {isEditMode && (
                <CardSection className="mt-2">
                  <Button
                    type="button"
                    onClick={handleDelete}
                    outline
                    color="red"
                    className="w-full border-transparent py-3 text-red-600 shadow-none hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Event
                  </Button>
                </CardSection>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
